import { Injectable, inject } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import moment from 'moment';
import {
    isEmpty as _isEmpty,
    toString as _toString,
    groupBy as _groupBy,
} from 'lodash';
import { UnifiedOrder } from '../../components/combined-orders/unified-order';
import { InvoiceParameter } from '../print-sheet/interfaces/invoice-param';
import { TaxInvoicePrintSheetParameter } from '../print-sheet/interfaces/tax-invoice-param';
import { UnifiedQueryV2Service } from '../unified-query-v2.service';
import { OutputTaxService } from '../output-tax/tax-output.service';
import {
    storeById,
    StoreId,
    taxInvoiceEntityIdFor,
} from '../../config/store-config';
import type { EntityId } from '../../config/store-config';
import { entityById } from '../../config/entity-config';
import {
    refuseTaxInvoice,
    validateFullTaxInvoice,
} from '../../config/tax-invoice-guards';
import {
    TaxInvoiceBuyer,
    toOutputTaxRow,
    toTaxInvoicePage,
    toTaxInvoicePageFromRow,
} from './build-tax-invoice';

// One order the pack run wants a tax invoice for, with everything the
// document needs already resolved by the combined-orders pipeline.
export interface TaxInvoiceIssueRequest {
    order: UnifiedOrder;
    invParam: InvoiceParameter;
    // Confirmed buyer details — the partition in combined-orders never sends
    // an order here without them (no ใบกำกับภาษีอย่างย่อ, ever).
    buyer: TaxInvoiceBuyer;
    // The customer explicitly asked for a tax invoice (billing request with
    // requestType 'tax-invoice') — lets the store's
    // requestedTaxInvoiceEntityId issue when the date-resolved owner cannot.
    hasTaxInvoiceRequest: boolean;
}

export interface TaxInvoiceIssueTarget {
    order: UnifiedOrder;
    entityId: EntityId;
    invNum: string;
    page: TaxInvoicePrintSheetParameter;
    // true when this run minted the row (the archive step persists these);
    // false when an existing non-voided row was reprinted.
    generated: boolean;
}

export interface TaxInvoiceIssueResult {
    targets: TaxInvoiceIssueTarget[];
    skipped: { order: UnifiedOrder; reason: string }[];
    warnings: string[];
}

// Fetch-or-generate (spec W14): for each order, an existing non-voided
// output-tax row keyed (platform, storeId, orderSn) is REPRINTED from what
// was stored — the row records what was printed — and only orders with no
// row get a new one saved (which mints the entity's next number) and a fresh
// page built. Re-running a pack selection therefore never issues a second
// invoice for the same order; a voided row no longer carries its orderSn, so
// a legitimate re-issue after a void mints a new number by the same lookup.
@Injectable({ providedIn: 'root' })
export class TaxInvoiceIssueService {
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private outputTaxService = inject(OutputTaxService);

    private static readonly GENERATE_CONCURRENCY = 3;

    public ensureTaxInvoices(
        requests: TaxInvoiceIssueRequest[],
    ): Observable<TaxInvoiceIssueResult> {
        if (_isEmpty(requests)) {
            return of({ targets: [], skipped: [], warnings: [] });
        }
        return this.fetchExistingRows(requests).pipe(
            switchMap((existingByKey) => {
                const skipped: TaxInvoiceIssueResult['skipped'] = [];
                const warnings: string[] = [];
                const tasks = requests.map((request) =>
                    this.ensureOne(request, existingByKey, skipped, warnings),
                );
                return from(tasks).pipe(
                    mergeMap(
                        (task) => task,
                        TaxInvoiceIssueService.GENERATE_CONCURRENCY,
                    ),
                    toArray(),
                    map((targets) => ({
                        // mergeMap loses the selection order; restore it so
                        // the tax-invoice stack pairs with the label stack.
                        targets: this.inRequestOrder(
                            targets.filter(
                                (t): t is TaxInvoiceIssueTarget => t !== null,
                            ),
                            requests,
                        ),
                        skipped,
                        warnings,
                    })),
                );
            }),
        );
    }

    // Takes the order number explicitly because the two shapes that meet in
    // the lookup name it differently: a UnifiedOrder carries `orderId`, a
    // stored output-tax row carries `orderSn`. An object parameter here once
    // let a row's absent `orderId` silently key every stored row as
    // 'platform:storeId:' and break the fetch-or-generate idempotency.
    private orderKey(platform: string, storeId: string, sn: string): string {
        return `${platform}:${storeId}:${_toString(sn)}`;
    }

    // One query for the whole batch. Voided rows carry voidedOrderSn instead
    // of orderSn, so they never match — exactly the "voided = miss" rule.
    private fetchExistingRows(
        requests: TaxInvoiceIssueRequest[],
    ): Observable<Map<string, any>> {
        const conditions = requests.map((request) => ({
            platform: request.order.platform,
            storeId: request.order.storeId,
            orderSn: _toString(request.order.orderId),
        }));
        return this.unifiedQueryV2Service
            .query<any>({
                db: 'output_tax',
                col: 'orders',
                query: { $or: conditions }, // eslint-disable-line @typescript-eslint/naming-convention
            })
            .pipe(
                switchMap((rows: any[]) => {
                    const invNums = rows
                        .map((row) => row?.orderInfo?.invNum)
                        .filter(Boolean);
                    if (_isEmpty(invNums)) {
                        return of({
                            rows,
                            itemsByInvNum: {} as Record<string, any[]>,
                        });
                    }
                    return this.unifiedQueryV2Service
                        .query<any>({
                            db: 'output_tax',
                            col: 'order_products',
                            query: { invNum: { $in: invNums } },
                        })
                        .pipe(
                            map((items: any[]) => ({
                                rows,
                                itemsByInvNum: _groupBy(items, 'invNum'),
                            })),
                        );
                }),
                map(({ rows, itemsByInvNum }) => {
                    const byKey = new Map<string, any>();
                    for (const row of rows) {
                        byKey.set(
                            this.orderKey(
                                row.platform,
                                row.storeId,
                                row.orderSn,
                            ),
                            {
                                ...row,
                                items:
                                    itemsByInvNum[row?.orderInfo?.invNum] ?? [],
                            },
                        );
                    }
                    return byKey;
                }),
            );
    }

    private ensureOne(
        request: TaxInvoiceIssueRequest,
        existingByKey: Map<string, any>,
        skipped: TaxInvoiceIssueResult['skipped'],
        warnings: string[],
    ): Observable<TaxInvoiceIssueTarget | null> {
        const { order, invParam, buyer, hasTaxInvoiceRequest } = request;
        const label = `${order.storeTitle} ${order.orderId}`;

        const existing = existingByKey.get(
            this.orderKey(order.platform, order.storeId, order.orderId),
        );
        if (existing) {
            return of({
                order,
                entityId: existing.entityId,
                invNum: _toString(existing?.orderInfo?.invNum),
                page: toTaxInvoicePageFromRow(existing, {
                    matchingNumber: order.matching,
                }),
                generated: false,
            } as TaxInvoiceIssueTarget);
        }

        // Resolved AT the order date — the tax point the document will
        // carry: the owner when it is registered at that date, else the
        // store's requested-tax-invoice issuer. null should not reach here
        // (the combined-orders partition sends such orders down the plain
        // path), so it is a named skip, never a throw.
        const dateIso = moment(order.createDate).format('YYYY-MM-DD');
        const entityId = taxInvoiceEntityIdFor(
            storeById(order.storeId as StoreId),
            dateIso,
            hasTaxInvoiceRequest,
        );
        if (!entityId) {
            skipped.push({
                order,
                reason: `no VAT-registered issuer at ${dateIso}`,
            });
            return of(null);
        }
        const entity = entityById(entityId);
        const row = toOutputTaxRow(
            {
                storeId: order.storeId,
                platform: order.platform,
                orderId: order.orderId,
                createDate: order.createDate as Date,
            },
            invParam,
            buyer,
            entity,
        );

        // W3 hard guards — a refusal skips the order (counted and surfaced),
        // never silently, and the batch continues.
        const refusal = refuseTaxInvoice({
            entityId: entity.id,
            storeId: order.storeId,
            sellerTaxId: row['sellerInfo.taxId'],
            invoiceDateIso: dateIso,
            isTaxInvoice: true,
        });
        if (refusal) {
            skipped.push({ order, reason: refusal });
            return of(null);
        }

        // W13 content validation — WARN severity.
        for (const warning of validateFullTaxInvoice(buyer)) {
            warnings.push(`${label}: ${warning}`);
        }

        return this.outputTaxService.saveTaxInvoice(row).pipe(
            map((response): TaxInvoiceIssueTarget => {
                if (response.status !== 200 || !response.invNum) {
                    throw new Error(`Save failed for ${label}`);
                }
                return {
                    order,
                    entityId: entity.id,
                    invNum: response.invNum,
                    page: toTaxInvoicePage(
                        row,
                        response.invNum,
                        order.matching,
                    ),
                    generated: true,
                };
            }),
            catchError((error) => {
                skipped.push({
                    order,
                    reason: `save failed: ${_toString(
                        (error as Error)?.message ?? error,
                    )}`,
                });
                return of(null);
            }),
        );
    }

    private inRequestOrder(
        targets: TaxInvoiceIssueTarget[],
        requests: TaxInvoiceIssueRequest[],
    ): TaxInvoiceIssueTarget[] {
        const byKey = new Map(
            targets.map((target) => [
                this.orderKey(
                    target.order.platform,
                    target.order.storeId,
                    target.order.orderId,
                ),
                target,
            ]),
        );
        return requests
            .map((request) =>
                byKey.get(
                    this.orderKey(
                        request.order.platform,
                        request.order.storeId,
                        request.order.orderId,
                    ),
                ),
            )
            .filter((t): t is TaxInvoiceIssueTarget => t !== undefined);
    }
}
